/*
 * @FileName       : BasePage.js
 * @Version        : 1.0
 * @DateOfCreation : 18/11/2019
 * @Author         : $u^^!t @ Shop Elect
 * @Dependencies   : N/A
 * @Description    : Base Page Component which wraps entire html contents of all pages, and includes global light or dark theme.
 *
 * Copyright (c) 2019, Shop Elect. All rights reserved.
 */

import React from 'react';
import { connect, useDispatch } from 'react-redux';
import MediaQuery, { useMediaQuery } from 'react-responsive';
import clsx from 'clsx';
import { CssBaseline, Drawer, AppBar, Toolbar, List, Divider, IconButton, MenuList, Fade } from '@material-ui/core';
import MenuIcon from '@material-ui/icons/Menu';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import SideMenuBar from './sideBar';
import MenuListComposition from './menuList';
import Loading from '../../../components/loading';
import { IMAGES_URL } from '../../../constants/Images';
import { BasePageStyles } from '../../../hooks/styles';
import { ReportActions, TenantsActions, UIAction, UserActions, UserManagementActions } from '../../../redux/actions';
import { Alert, AlertTitle } from '@material-ui/lab';
import usePoll from 'react-use-poll';
import { useHistory } from 'react-router-dom';

const BasePage = (props: any) => {
	const history = useHistory();
	const dispatch = useDispatch();
	const isDesktopOrLaptop = useMediaQuery({
		query: '(min-device-width: 1224px)',
	});

	const checkQueue = () => {
		props.getUserReportQueue(); //this method checks and only makes the server call if we are awaiting new reports!
	};

	usePoll(() => checkQueue(), [], {
		interval: 10000, //should be async but brute force delay for now
	});

	const initNavDrawerState = () => {
		let open = isDesktopOrLaptop ? true : false;
		if (props.navDrawerOpen === false) {
			open = false;
		}
		return open;
	};

	const classes = BasePageStyles();
	const [open, setOpen] = React.useState(initNavDrawerState());
	const [urlIsProcessed, setUrlIsProcessed] = React.useState(false);
	const [isCloudUser, setIsCloudUser] = React.useState(false);

	React.useEffect(() => {
		if (!urlIsProcessed) {
			props.setUrlFilter(window.location.search);
			setUrlIsProcessed(true);
		}
	}, [urlIsProcessed]);

	const handleDrawerOpen = () => {
		setOpen(true);
		props.setNavDrawer(true);
	};

	const handleDrawerClose = () => {
		setOpen(false);
		props.setNavDrawer(false);
	};

	const clearError = () => {
		dispatch(UIAction.setApiError(null));
	};

	const clearNavigationMenu = () => {
		dispatch(UserManagementActions.getUserListCompleteAction([]));
		dispatch(UIAction.setApiError(null));
		dispatch(ReportActions.clearInventoryGroup());
		dispatch(ReportActions.clearRecentlyVended());
		dispatch(TenantsActions.clearTenantsList());
		dispatch(
			UIAction.setNavigationState({
				title: 'Dashboard',
				url: '/dashboard',
			}),
		);
	};

	return (
		<div className={clsx(classes.root, props.isLoading ? 'show-loader' : '')}>
			<CssBaseline />

			<AppBar position="absolute" elevation={0} className={clsx(classes.appBar, open && classes.appBarShift)}>
				{/* app top bar */}

				<Toolbar className={isDesktopOrLaptop ? classes.toolbar : classes.toolbarMobile}>
					<IconButton
						edge="start"
						color="inherit"
						aria-label="open drawer"
						onClick={handleDrawerOpen}
						className={clsx(
							isDesktopOrLaptop ? classes.menuButton : classes.menuButtonMobile,
							open && classes.menuButtonHidden,
						)}
					>
						<MenuIcon />
					</IconButton>
					<MediaQuery minDeviceWidth={1224}>
						{props.title && (
							<MenuList className={classes.topmenu}>
								<h3>{props.title}</h3>
							</MenuList>
						)}
						<MenuListComposition />
						{/* contains user avatar and profile edit link */}
					</MediaQuery>
					<MediaQuery maxDeviceWidth={1223}>
						<Fade in={!open}>
							<div
								style={{
									width: '73%',
									height: '56px',
								}}
							>
								{props.title && (
									<h3 className={classes.pageTitle} style={props.selectedCustomer ? { margin: '5px 0 0 0' } : {}}>
										{props.title}
									</h3>
								)}
							</div>
						</Fade>
						<MenuListComposition />
					</MediaQuery>
				</Toolbar>
			</AppBar>
			<Drawer
				variant="permanent"
				classes={{
					paper: clsx(classes.leftColumn, classes.drawerPaper, !open && classes.drawerPaperClose),
				}}
				open={open}
				elevation={0}
			>
				<div className={classes.toolbarIcon}>
					<img
						src={isCloudUser ? IMAGES_URL.RNI_CLOUD_LOGO : IMAGES_URL.RNI_LOGO_SM}
						className="logo-image"
						alt="fdi"
						style={{ cursor: 'pointer' }}
						onClick={() => {
							history.push('/dashboard');
							clearNavigationMenu();
						}}
					/>
					<IconButton onClick={handleDrawerClose}>
						<ChevronLeftIcon />
					</IconButton>
				</div>

				<List
					style={{
						height: '100%',
					}}
				>
					<SideMenuBar open={open} setOpen={setOpen} isCloudUser={setIsCloudUser} />
				</List>
			</Drawer>

			{urlIsProcessed && (
				<main className={classes.content}>
					{props.children}
					{props.errorMessage && (
						<div onClick={clearError} className={classes.errorAlert}>
							<Alert severity={props.errorMessage.severity}>
								<AlertTitle>{props.errorMessage.title}</AlertTitle>
								{props.errorMessage.errorMessage}
							</Alert>
						</div>
					)}
				</main>
			)}
			{props.isLoading ? <Loading message="Loading..." /> : ''}
		</div>
	);
};

const mapStateToProps = (state: any) => ({
	activeMenu: state.ui.menuState,
	isLoading: state.ui.isLoading,
	errorMessage: state.ui.errorMessage,
	navDrawerOpen: state.ui.navDrawerOpen,
	selectedCustomer: state.ui.selectedCustomer,
	selectedTheme: state.user.selectedTheme,
	awaitingQueueItemCount: state.user?.awaitingQueueItemCount || 0,
});

const mapDispatchToProps = (dispatch: any, ownProps: any) => ({
	setNavDrawer: (open: boolean) => dispatch(UIAction.setNavDrawer(open)),
	setUrlFilter: (queryString: string) => dispatch(UIAction.setUrlFilter(queryString)),
	getUserReportQueue: () => dispatch(UserActions.getUserReportQueue()),
});

export default connect(mapStateToProps, mapDispatchToProps)(BasePage);
